import {
  CalendarOutlined,
  DeleteOutlined,
  GroupOutlined,
  LoadingOutlined,
  LockOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Avatar,
  Card,
  Tooltip,
  List,
  Popconfirm,
  Button,
  Flex,
  Typography,
  Tag,
} from 'antd'
import { format } from 'date-fns'
import React from 'react'
import { Link } from 'react-router-dom'
import { useGroup } from '~/hooks/Groups/useGroup'

import { useUserGroup } from '~/hooks/Groups/useUserGroup'
import { LoadingIndicator } from '~/pages/Scale/EditScale/styles'
import { createLetterAvatar } from '~/utils/letter'
import { translate } from '~/utils/locale'
import CreateUser from '../Create/UserGroup'
import EditGroup from '../Update/Group'
import ContributorPermission from '../Create/ContributorPermission'

const DetailsGroup: React.FC = () => {
  const { groups, isLoading } = useGroup()
  const { deleteUser } = useUserGroup()

  const { Title, Text } = Typography

  if (isLoading || !groups) {
    return (
      <LoadingIndicator>
        <LoadingOutlined style={{ fontSize: 64, margin: 'auto' }} spin />
      </LoadingIndicator>
    )
  }

  const formattedDate = format(
    new Date(groups.created_at),
    `dd/MM/yyyy '${translate('GroupsDetails.timeString')}' HH:mm'h'`,
  )

  return (
    <>
      <Card
        bordered={false}
        title={
          <Title level={5} style={{ marginBottom: 0, fontSize: 18 }}>
            {translate('GroupsDetails.cardTitle')}
          </Title>
        }
        extra={
          groups.type === 'admin' ||
          groups.type === 'user' ||
          groups.type === 'manager' ? (
            ''
          ) : (
            <EditGroup id_group={groups.id} name_group={groups.name} />
          )
        }
        style={{
          display: 'inline-block',
          width: '100%',
        }}
      >
        <Flex gap={20}>
          <Avatar
            shape="square"
            size={120}
            style={{
              fontSize: 30,
              color: '#fff',
              background: 'linear-gradient(135deg, #3C8ACB 0%, #3969B7 100%)',
              borderRadius: 12,
            }}
          >
            {createLetterAvatar(`${groups.name}`)}
          </Avatar>
          <Flex vertical justify="space-around">
            <Flex gap={60}>
              <Flex gap={10} style={{ minWidth: 250 }}>
                <Avatar
                  shape="square"
                  icon={
                    <GroupOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    Nome do Grupo
                  </Title>
                  <Text>{groups.name}</Text>
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 250 }}>
                <Avatar
                  shape="square"
                  icon={
                    <CalendarOutlined
                      style={{ fontSize: 12, color: '#464545' }}
                    />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {translate('GroupsDetails.createdText')}
                  </Title>
                  {formattedDate}
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 250 }}>
                <Avatar
                  shape="square"
                  icon={
                    <UserOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    Tipo do Grupo
                  </Title>
                  {groups.type === 'admin' ? (
                    <Tag color="#1565C0">
                      {translate('GroupsTable.groupTypeAdmin')}
                    </Tag>
                  ) : (
                    ''
                  )}
                  {groups.type === 'custom' ? (
                    <Tag color="#439A5F">
                      {translate('GroupsTable.groupTypeCustom')}
                    </Tag>
                  ) : (
                    ''
                  )}
                  {groups.type === 'user' ? (
                    <Tag color="#6A1B9A">
                      {translate('GroupsTable.groupTypeUser')}
                    </Tag>
                  ) : (
                    ''
                  )}
                  {groups.type === 'manager' ? (
                    <Tag color="#D04141">
                      {translate('GroupsTable.groupTypeManager')}
                    </Tag>
                  ) : (
                    ''
                  )}
                </div>
              </Flex>
            </Flex>

            <Flex gap={60}>
              <Flex gap={10} style={{ minWidth: 250 }}>
                <Avatar
                  shape="square"
                  icon={
                    <LockOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    Permissões
                  </Title>
                  <Tag color="#616161">
                    {groups.permissions.length}{' '}
                    {translate('GroupsDetails.permissionsText')}
                  </Tag>
                </div>
              </Flex>

              <Flex gap={10} style={{ minWidth: 250 }}>
                <Avatar
                  shape="square"
                  icon={
                    <UserOutlined style={{ fontSize: 12, color: '#464545' }} />
                  }
                  size="small"
                  style={{ background: '#DADADA' }}
                />
                <div>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    Membros
                  </Title>
                  <Tag color="#616161">
                    {groups.userGroups.length}{' '}
                    {translate('GroupsDetails.membersText')}
                  </Tag>
                </div>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <Flex gap={10} align="stretch">
        <Card
          bordered={false}
          title={
            <Title level={5} style={{ marginBottom: 0, fontSize: 18 }}>
              {translate('GroupsDetails.permissionUsersCardTitle')}
            </Title>
          }
          extra={<CreateUser group_id={groups.id} />}
          style={{
            display: 'inline-block',
            width: '100%',
            marginTop: 14,
          }}
        >
          <List
            itemLayout="horizontal"
            dataSource={groups.userGroups}
            size="small"
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Popconfirm
                    key="actions"
                    title={translate('GroupsDetails.deletePopconfirm')}
                    onConfirm={() => deleteUser(item.user.id, groups.id)}
                  >
                    <Tooltip title={translate('GroupsDetails.deleteTooltip')}>
                      <Button
                        ghost
                        shape="circle"
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                      />
                    </Tooltip>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={
                    <Tooltip title={translate('GroupsDetails.viewTooltip')}>
                      {item.user.contact && (
                        <Link
                          to={`/colaboradores/visualizar/colaborador/${item.user.contact?.collaborator?.id}/contato/${item.user.contact?.id}`}
                        >
                          {item.user?.contact.name}
                        </Link>
                      )}
                    </Tooltip>
                  }
                />
              </List.Item>
            )}
          />
        </Card>

        <Card
          bordered={false}
          title={translate('GroupsDetails.permissionContributorsCardTitle')}
          extra={<ContributorPermission group_id={groups.id} />}
          style={{
            display: 'inline-block',
            width: '100%',
            marginTop: 14,
          }}
        >
          <List
            itemLayout="horizontal"
            dataSource={groups.userGroups}
            size="small"
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Popconfirm
                    key="actions"
                    title={translate(
                      'GroupsDetails.deleteContributorPopconfirm',
                    )}
                    onConfirm={() => deleteUser(item.user.id, groups.id)}
                  >
                    <Tooltip
                      title={translate(
                        'GroupsDetails.deleteContributorTooltip',
                      )}
                    >
                      <Button
                        ghost
                        shape="circle"
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                      />
                    </Tooltip>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={
                    <Tooltip
                      title={translate('GroupsDetails.viewContributorTooltip')}
                    >
                      {item.user.contact && (
                        <Link
                          to={`/colaboradores/visualizar/colaborador/${item.user.contact?.collaborator?.id}/contato/${item.user.contact?.id}`}
                        >
                          {item.user?.contact.name}
                        </Link>
                      )}
                    </Tooltip>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      </Flex>
    </>
  )
}

export default DetailsGroup
